import { Product } from "@decocms/apps-commerce/types";
import { useCart } from "~/hooks/useCart";
import { useEffect, useState } from "react";
import type { Props } from "~/components/product/BuyTogether";
import type { IParams } from "~/components/product/types";
import DiscountBadge from "~/components/ui/DiscountBadge";
import Icon from "~/components/ui/Icon";
import ProductCard from "~/components/product/ProductCard";
import { invoke } from "~/runtime";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { useOffer } from "~/sdk/useOffer";
import { useUI } from "~/sdk/useUI";

type SellerMerchantInstallment = {
  id: string;
  count: number;
  hasInterestRate: boolean;
  interestRate: number;
  value: number;
  total: number;
};

type MaxInstallments = {
  count: number;
  hasInterestRate: boolean;
  interestRate: number;
  value: number;
  total: number;
  sellerMerchantInstallments: SellerMerchantInstallment[];
};

type PaymentDataInstallmentOption = {
  installments: MaxInstallments[];
};

type ProductAttachmentsResponse = {
  paymentData?: {
    installmentOptions: PaymentDataInstallmentOption[];
  };
};

function BuyTogether({ products, hideButton }: Props) {
  const productList = products ?? [];
  const getAvailableVariant = (product: Product): Product => {
    const variants = product.isVariantOf?.hasVariant ?? [];
    const availableVariant = variants.find((variant) =>
      (variant.offers?.offers?.[0]?.inventoryLevel?.value ?? 0) > 0
    );

    if (!availableVariant) {
      return product;
    }

    return {
      ...product,
      ...availableVariant,
    } as Product;
  };

  const productsSortedByAvailability = productList.map(getAvailableVariant);

  const [selectedItems, setSelectedItems] = useState<Product[]>(
    productsSortedByAvailability,
  );
  const [productAvailable, setProductAvailable] = useState<boolean>();
  const [productParams, setProductParams] = useState<IParams>({
    size: "",
    color: "",
  });
  const [maxInstallment, setMaxInstallment] = useState<MaxInstallments | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const { addItems } = useCart();
  const { displayCart } = useUI();

  const onClickAddProducts = async () => {
    if (selectedItems.length === 0) {
      return;
    }

    setLoading(true);

    const orderItems = selectedItems.map((item) => ({
      id: item.productID,
      seller: "1",
      quantity: 1,
    }));

    try {
      await addItems({ orderItems });
    } catch (error) {
      console.error("Erro ao adicionar itens ao carrinho:", error);
    } finally {
      setLoading(false);
      displayCart.value = true;
    }
  };

  useEffect(() => {
    if (typeof selectedItems !== "undefined" && selectedItems.length > 0) {
      const items = selectedItems.map((item) => ({
        id: item.productID,
        quantity: 1,
        seller: 1,
      }));

      const getProductAttachment = async () => {
        try {
          const response = await invoke[
            "site"
          ].actions.getProductAttachments({ items }) as ProductAttachmentsResponse;

          if (response && response.paymentData) {
            const installmentOptions = response.paymentData.installmentOptions
              .map((payment: PaymentDataInstallmentOption) => {
                return payment.installments.filter(
                  (installment: MaxInstallments) => !installment.hasInterestRate,
                );
              });

            const flattenedOptions = installmentOptions.flat();
            const bestOption = flattenedOptions.reduce(
              (max: MaxInstallments, option: MaxInstallments) => {
                return option.count > max.count ? option : max;
              },
              {
                count: 0,
                hasInterestRate: false,
                interestRate: 0,
                value: 0,
                total: 0,
                sellerMerchantInstallments: [],
              },
            );

            setMaxInstallment(bestOption);
          }
        } catch (error) {
          console.error("Error fetching payment data:", error);
        }
      };

      getProductAttachment();
    } else {
      setMaxInstallment(null);
    }
  }, [selectedItems]);

  const selectedAVariation = productParams.size != "" &&
    productParams.color != "";
  const isTwoProductsLayout = productList.length === 2;
  const isThreeProductsLayout = productList.length === 3;
  const getProductSelectionKey = (product: Product) =>
    product.inProductGroupWithID ?? product.isVariantOf?.productGroupID ??
      product.productID;
  const toggleSelectedItem = (product: Product) => {
    const productKey = getProductSelectionKey(product);

    setSelectedItems((currentItems) => {
      const isSelected = currentItems.some((item) =>
        getProductSelectionKey(item) === productKey
      );

      if (isSelected) {
        return currentItems.filter((item) =>
          getProductSelectionKey(item) !== productKey
        );
      }

      return [...currentItems, getAvailableVariant(product)];
    });
  };

  return (
    <div
      className={`buyTogether flex w-full flex-col gap-8 lg:flex-row lg:items-start lg:justify-between ${
        isThreeProductsLayout
          ? "buyTogether--three"
          : isTwoProductsLayout
          ? "buyTogether--two"
          : ""
      }`}
    >
      <ul
        className={`buyTogetherProducts w-full flex flex-wrap max-lg:grid max-lg:grid-cols-2 justify-around gap-2 gap-y-20 lg:flex-nowrap lg:items-start ${
          isThreeProductsLayout
            ? "lg:w-auto lg:justify-start lg:gap-8"
            : isTwoProductsLayout
            ? "lg:w-auto lg:grid lg:grid-cols-2 lg:gap-x-16 lg:min-h-[622px]"
            : "lg:grid lg:grid-cols-3 lg:gap-y-48 lg:min-h-[622px]"
        }`}
      >
        {productList.map((product: Product, index) => {
          const { listPrice, price = 0 } = useOffer(product.offers);
          const isProductSelected = selectedItems.some((item) =>
            getProductSelectionKey(item) === getProductSelectionKey(product)
          );

          return (
            <li
              key={index}
              className={`buyTogetherItem flex items-center justify-around max-w-[170px] sm:max-w-max ${
                isThreeProductsLayout
                  ? "lg:h-95 lg:max-w-none lg:gap-8"
                  : isTwoProductsLayout
                  ? "lg:h-[450px] lg:gap-12"
                  : "lg:h-[450px] lg:gap-2"
              }`}
            >
              <div
                className={`buyTogetherCard relative h-full ${
                  isThreeProductsLayout ? "lg:w-[214px]" : "lg:w-[310px]"
                }`}
              >
                <DiscountBadge
                  listPrice={listPrice}
                  price={price}
                  variant="dark"
                />
                <ProductCard
                  product={product}
                  setSelectedItems={setSelectedItems}
                  setProductAvailable={setProductAvailable}
                  setProductParams={setProductParams}
                  hideButton={hideButton}
                  hideDiscountBadge
                />
                <button
                  type="button"
                  aria-label={isProductSelected
                    ? "Remover produto do look"
                    : "Adicionar produto ao look"}
                  aria-pressed={isProductSelected}
                  className="absolute right-2 top-2 z-20 flex h-[17px] w-[17px] items-center justify-center"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleSelectedItem(product);
                  }}
                >
                  {isProductSelected
                    ? (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="17" height="17" fill="white" />
                        <path
                          d="M0 0V17H17V0H0ZM6.94712 12.7655L3.65337 9.10522L4.62555 8.23029L6.91401 10.7729L12.3413 4.30966L13.3442 5.14904L6.94712 12.7655Z"
                          fill="black"
                        />
                      </svg>
                    )
                    : <span className="block h-[17px] w-[17px] border border-black bg-white" />}
                </button>
              </div>
              {index < productList.length - 1
                ? (
                  <div
                    aria-hidden="true"
                    className={`buyTogetherOperator ${
                      index == 1 ? "max-lg:hidden" : ""
                    } ${
                      productList.length === 3
                        ? "iconPlusThreeProducts"
                        : "iconPlusBuyTogether"
                    }`}
                  >
                    <Icon id="Plus" size={26} strokeWidth={2} />
                  </div>
                )
                : (
                  <div
                    aria-hidden="true"
                    className={`buyTogetherOperator ${
                      productList.length === 3
                        ? "iconEqualThreeProducts"
                        : "iconEqualBuyTogether"
                    }`}
                  >
                    <span className="text-[50px] leading-none">=</span>
                  </div>
                )}
            </li>
          );
        })}
      </ul>
      <div className="buyTogetherSummary flex gap-2 items-center justify-center lg:justify-start w-full lg:w-auto lg:pt-[150px]">
        <div className="flex flex-col w-full lg:w-auto">
            <div className="h-full px-4 py-0 lg:p-0 flex flex-col lg:min-w-[311px]">
              <div className="flex flex-col items-center justify-center gap-1 mb-2 min-h-24">
                <p className="text-[#333333] text-xl font-medium uppercase">
                  Leve o look por:
                </p>
                {maxInstallment && (
                  <>
                    <p className="text-black text-xl font-bold m-0">
                      {formatPrice(maxInstallment.total / 100)}
                    </p>
                    <p className="text-[#898989] text-base m-0">
                      ou até {maxInstallment.count}x de{" "}
                      {formatPrice(maxInstallment.value / 100)}
                    </p>
                  </>
                )}
              </div>
            </div>
            {selectedAVariation
              ? (
                <div className="px-2 lg:px-0 flex">
                  <button
                    id="buyButtonBuyTogether"
                    className="bg-[#12100C] hover:bg-[#CCC184] text-white text-base font-bold lg:max-w-[262px] m-auto w-full h-[55px] uppercase tracking-[5.2px]"
                    onClick={onClickAddProducts}
                    disabled={loading || !productAvailable ||
                      selectedItems.length === 0}
                  >
                    {loading ? "comprando..." : "Comprar look"}
                  </button>
                </div>
              )
              : (
                <div
                  id="buyButton"
                  className={`w-full gap-2 flex
               h-[55px] cursor-pointer self-center text-center py-1 px-2 lg:px-0`}
                >
                  <button
                    onClick={() => alert("Selecione cor e tamanho!")}
                    className="font-['Poppins'] inline-block w-full font-bold not-italic lg:max-w-[262px] bg-[#1A1A1A] hover:bg-[#CCC184] leading-[17px] tracking-[0.325em] text-[#FFFFFF] uppercase text-base md:py-[15px] h-[55px] m-auto"
                  >
                    Comprar Look
                  </button>
                </div>
              )}
        </div>
      </div>
    </div>
  );
}

export default BuyTogether;
